import { useEffect, useRef, useState } from 'react';
import {
  IconBell, IconCreditCard, IconCash, IconPhoneCall,
  IconCheck, IconX, IconChevronRight, IconClock, IconUser,
} from '@tabler/icons-react';

/**
 * PendingRequestsBar — sticky bar at the top of /pos.
 *
 * Polls /api/table-requests every 3s. Plays a soft chime + flashes
 * red on transitions where a new PENDING arrives. Lets the staff
 * member tap "Accept" on any row to claim it for their till.
 *
 * The parent (PosPage) receives the accepted request's cart snapshot
 * via the onAccept callback and is responsible for loading it into
 * the active till cart.
 */

export interface PendingRequest {
  id: string;
  kind: 'ORDER' | 'ASSISTANCE';
  status: 'PENDING' | 'ACCEPTED' | 'CANCELLED' | 'EXPIRED' | 'PAID';
  orderCode: string;
  paymentIntent: 'CARD_AT_TABLE' | 'CASH_AT_COUNTER' | null;
  cartSnapshot: PendingCartLine[] | null;
  totalCents: number | null;
  note: string | null;
  submittedAt: string;
  ageSeconds: number;
  table: { id: string; name: string; area: string | null; code: string };
  acceptedBy: { id: string; name: string } | null;
}

export interface PendingCartLine {
  productId: string;
  name: string;
  size: string;
  priceDelta: number;
  basePrice: number;
  excludes: string[];
  addons: { name: string; priceCents: number }[];
  note?: string;
  qty: number;
}

function fmtR(cents: number): string {
  return `R ${(cents / 100).toFixed(2)}`;
}
function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
}

/** A short, friendly chime using the Web Audio API — no external file. */
function playChime() {
  try {
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Two tones — a major third up.
    [880, 1108.73].forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.25, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.5);
    });
    // Auto-close to free resources.
    window.setTimeout(() => { void ctx.close(); }, 1500);
  } catch { /* silent */ }
}

interface Props {
  onAccept: (request: PendingRequest) => void;
}

export default function PendingRequestsBar({ onAccept }: Props) {
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const lastIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch('/api/table-requests?status=open', { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const next: PendingRequest[] = data.requests ?? [];
        // Detect newly-arrived PENDING items for chime.
        const newPending = next.filter((x) => x.status === 'PENDING' && !lastIdsRef.current.has(x.id));
        if (newPending.length > 0 && lastIdsRef.current.size > 0) {
          playChime();
        }
        lastIdsRef.current = new Set(next.filter((x) => x.status === 'PENDING').map((x) => x.id));
        setRequests(next);
      } catch { /* silent */ }
    }
    void load();
    const id = window.setInterval(load, 3000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  async function accept(req: PendingRequest) {
    setAccepting(req.id);
    setErrorMsg(null);
    try {
      const r = await fetch(`/api/table-requests/${req.id}/accept`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error ?? `HTTP ${r.status}`);
      onAccept(req);
      // Optimistic local mark — server side already marked.
      setRequests((prev) => prev.map((x) => x.id === req.id ? { ...x, status: 'ACCEPTED' } : x));
      setExpanded(false);
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setAccepting(null);
    }
  }

  async function dismiss(req: PendingRequest) {
    try {
      await fetch(`/api/table-requests/${req.id}/dismiss`, {
        method: 'POST', credentials: 'include',
      });
      setRequests((prev) => prev.filter((x) => x.id !== req.id));
    } catch { /* silent */ }
  }

  if (requests.length === 0) return null;

  const pending = requests.filter((r) => r.status === 'PENDING');
  const accepted = requests.filter((r) => r.status === 'ACCEPTED');
  const showCount = pending.length;

  return (
    <div className="relative z-30 border-b-2 border-border bg-warning-soft">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((o) => !o)}
        className="w-full px-5 py-2.5 flex items-center gap-3 hover:bg-warning-soft/80 transition-colors text-left"
      >
        <div className={`relative w-9 h-9 rounded-md inline-flex items-center justify-center flex-shrink-0 ${showCount > 0 ? 'bg-warning text-[#FFFFFF]' : 'bg-light text-gray-700'}`}>
          <IconBell size={18} stroke={2.25} />
          {showCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-danger text-[#FFFFFF] text-9 font-bold tabular-nums ring-2 ring-warning-soft animate-pulse">
              {showCount}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-13 font-bold text-heading">
            {showCount > 0
              ? `${showCount} customer ${showCount === 1 ? 'request' : 'requests'} waiting`
              : `${accepted.length} accepted, awaiting completion`}
          </p>
          <p className="text-11 font-medium text-gray-700">
            {showCount > 0 ? 'Tap to view and accept' : 'Tap to view'}
          </p>
        </div>
        <IconChevronRight size={16} stroke={2.5} className={`text-gray-700 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {/* Expanded list */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2 max-h-[70vh] overflow-y-auto">
          {errorMsg && (
            <div className="rounded-md bg-danger-soft border border-danger/30 p-2.5 text-12 font-semibold text-danger">
              {errorMsg}
            </div>
          )}
          {requests.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              accepting={accepting === req.id}
              onAccept={() => void accept(req)}
              onDismiss={() => void dismiss(req)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({
  req, accepting, onAccept, onDismiss,
}: {
  req: PendingRequest;
  accepting: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const isOrder = req.kind === 'ORDER';
  const intent = req.paymentIntent;

  return (
    <div className="rounded-lg bg-surface border-2 border-border p-3 shadow-sm">
      {/* Header row: code, table, age, status */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {isOrder && (
          <span className="inline-flex items-center px-2 h-6 rounded-md bg-heading text-[#FFFFFF] text-13 font-bold tabular-nums">
            #{req.orderCode}
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-light border border-border text-11 font-bold uppercase tracking-wider text-heading">
          <IconUser size={11} stroke={2.5} />
          {req.table.name}{req.table.area ? ` · ${req.table.area}` : ''}
        </span>
        {isOrder && intent === 'CARD_AT_TABLE' && (
          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-info-soft text-info text-11 font-bold uppercase tracking-wider">
            <IconCreditCard size={11} stroke={2.5} />
            Card at table
          </span>
        )}
        {isOrder && intent === 'CASH_AT_COUNTER' && (
          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-warning-soft text-warning text-11 font-bold uppercase tracking-wider">
            <IconCash size={11} stroke={2.5} />
            Cash at counter
          </span>
        )}
        {!isOrder && (
          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-purple-soft text-purple text-11 font-bold uppercase tracking-wider">
            <IconPhoneCall size={11} stroke={2.5} />
            Assistance
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-11 font-medium text-gray-700 ml-auto">
          <IconClock size={11} stroke={2.25} />
          {fmtAge(req.ageSeconds)}
        </span>
        {req.status === 'ACCEPTED' && (
          <span className="inline-flex items-center px-2 h-6 rounded-md bg-success-soft text-success text-11 font-bold uppercase tracking-wider">
            Accepted by {req.acceptedBy?.name ?? '—'}
          </span>
        )}
      </div>

      {/* Cart preview */}
      {isOrder && req.cartSnapshot && req.cartSnapshot.length > 0 && (
        <ul className="space-y-0.5 mb-2 pl-1">
          {req.cartSnapshot.slice(0, 5).map((l, i) => (
            <li key={i} className="text-12 text-heading flex items-center justify-between gap-2">
              <span className="truncate">
                <span className="font-bold tabular-nums">{l.qty}×</span> {l.name}
                {l.size && l.size !== 'Regular' ? ` (${l.size})` : ''}
                {(l.excludes.length > 0 || l.addons.length > 0) && (
                  <span className="text-gray-700 italic">
                    {l.excludes.length > 0 ? ` — no ${l.excludes.join(', ')}` : ''}
                    {l.addons.length > 0 ? ` + ${l.addons.map((a) => a.name).join(', ')}` : ''}
                  </span>
                )}
              </span>
            </li>
          ))}
          {req.cartSnapshot.length > 5 && (
            <li className="text-12 italic text-gray-700 pl-1">+ {req.cartSnapshot.length - 5} more</li>
          )}
        </ul>
      )}

      {isOrder && req.totalCents != null && (
        <div className="flex items-center justify-between text-13 font-bold text-heading mt-2 mb-2 pt-2 border-t border-border-soft">
          <span>Total</span>
          <span className="tabular-nums">{fmtR(req.totalCents)}</span>
        </div>
      )}

      {req.note && (
        <p className="text-12 italic text-warning bg-warning-soft rounded p-2 mb-2">"{req.note}"</p>
      )}

      {/* Actions */}
      {req.status === 'PENDING' && (
        <div className="flex items-stretch gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="h-10 px-3 rounded-md bg-light border-2 border-border text-12 font-bold text-heading hover:border-danger hover:text-danger inline-flex items-center justify-center gap-1.5"
          >
            <IconX size={14} stroke={2.25} />
            Dismiss
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={accepting}
            className={[
              'flex-1 h-10 rounded-md text-13 font-bold inline-flex items-center justify-center gap-1.5 transition-colors',
              accepting
                ? 'bg-gray-300 text-gray-600'
                : isOrder
                  ? 'bg-success text-[#FFFFFF] hover:bg-success-hover'
                  : 'bg-primary text-[#FFFFFF] hover:bg-primary-hover',
            ].join(' ')}
          >
            <IconCheck size={14} stroke={2.5} />
            {accepting ? 'Accepting...' : isOrder ? 'Accept · load to cart' : 'Acknowledge'}
          </button>
        </div>
      )}
    </div>
  );
}
